import React, {useEffect, useRef} from 'react';

import {motion, useAnimation} from 'framer-motion';
import './styles';

const DEFAULT_DURATION = 2000;

type ProgressBarProps = {
  duration?: number;
  value?: number;
  onDurationEnds?: () => void;
  waitForReady?: boolean;
  isReady?: boolean;
  maxWhileWaiting?: number;
  readyCompletionDuration?: number;
};

const ProgressBar = ({
  duration = DEFAULT_DURATION,
  value = 0,
  onDurationEnds,
  waitForReady = false,
  isReady = false,
  maxWhileWaiting = 90,
  readyCompletionDuration = 400,
}: ProgressBarProps) => {
  const controls = useAnimation();
  const onDurationEndsRef = useRef(onDurationEnds);
  const hasInitializedRef = useRef(false);
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    onDurationEndsRef.current = onDurationEnds;
  }, [onDurationEnds]);

  useEffect(() => {
    let isCancelled = false;

    if (waitForReady) {
      if (!hasInitializedRef.current) {
        controls.set({width: `${value}%`});
        hasInitializedRef.current = true;
      }

      if (isReady) {
        if (hasCompletedRef.current) {
          return () => {
            isCancelled = true;
          };
        }

        controls
          .start({
            width: '100%',
            transition: {
              duration: readyCompletionDuration / 1000,
              ease: 'linear',
            },
          })
          .then(() => {
            if (!isCancelled) {
              hasCompletedRef.current = true;
              onDurationEndsRef.current?.();
            }
          });
      } else {
        if (!hasCompletedRef.current) {
          controls.start({
            width: `${maxWhileWaiting}%`,
            transition: {
              duration: duration / 1000,
              ease: 'linear',
            },
          });
        }
      }
    } else {
      controls.set({width: `${value}%`});
      controls
        .start({
          width: '100%',
          transition: {
            duration: duration / 1000,
            ease: 'linear',
          },
        })
        .then(() => {
          if (!isCancelled) {
            onDurationEndsRef.current?.();
          }
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [
    controls,
    duration,
    value,
    waitForReady,
    isReady,
    maxWhileWaiting,
    readyCompletionDuration,
  ]);

  return (
    <div className="progress-container">
      <motion.div
        className="progress-fill"
        animate={controls}
      />
    </div>
  );
};

export default ProgressBar;
